import React, { useState, useEffect } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Container, Row, Col, Badge, Card, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/widgets/Loader';
import axios from '../../services/axios';

const OPERATION_STATUS_VARIANT = {
    draft: 'secondary',
    active: 'success',
    closed: 'danger',
};

const OperationsPage = () => {
    const { apiData: { ACTIONS, BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const operationsSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('operations'));
    const { allTagsByUuid } = useStoreState(state => state.tagOperations);
    const { fetchTagOperations } = useStoreActions(actions => actions.tagOperations);
    const navigate = useNavigate();

    const { operationStatus = [], inscriptionStatus = [] } = BACKOFFICE_DEFAULT_VALUES;
    const operationStatusLabel = Object.fromEntries(operationStatus.map(s => [s.value, s.label]));
    const inscriptionStatusLabel = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.label]));

    const [loading, setLoading] = useState(true);
    const [operations, setOperations] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const url = ACTIONS?.operations?.readAll?.url;
        if (!url) return;
        axios.get(url)
            .then(data => setOperations(data))
            .catch(() => setError('Impossible de charger les opérations.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchTagOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOperationClick = (slug) => {
        navigate(operationsSlugs?.readOne?.replace(':uniqueId', slug) ?? `/operations/${slug}`);
    };

    if (loading) return <Loader />;
    if (error) return <Container fluid><Alert variant="danger" className="mt-3">{error}</Alert></Container>;

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <h2>Opérations spéciales</h2>
                </Col>
            </Row>
            {operations.length === 0
                ? <Alert variant="info">Aucune opération disponible.</Alert>
                : <Row>
                    {operations.map(op => (
                        <Col key={op.slug} md={6} lg={4} className="mb-4">
                            <Card
                                className="h-100"
                                onClick={() => handleOperationClick(op.slug)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Card.Body>
                                    <Card.Title className="d-flex justify-content-between align-items-start gap-2">
                                        <span>{op.nom}</span>
                                        <div className="d-flex gap-1 flex-wrap justify-content-end">
                                            <Badge bg={OPERATION_STATUS_VARIANT[op.statut] ?? 'secondary'}>
                                                {operationStatusLabel[op.statut] ?? op.statut}
                                            </Badge>
                                            {(op.tagOperations ?? []).map(uuid => {
                                                const tag = allTagsByUuid.get(uuid);
                                                return tag
                                                    ? <Badge key={uuid} bg="secondary">{tag.name}</Badge>
                                                    : null;
                                            })}
                                        </div>
                                    </Card.Title>
                                    <Card.Text as="div">
                                        <small className="text-muted">
                                            Du {op.dateDebut ? new Date(op.dateDebut).toLocaleDateString('fr-FR') : '—'}
                                            {' '}au {op.dateFin ? new Date(op.dateFin).toLocaleDateString('fr-FR') : '—'}
                                        </small>
                                    </Card.Text>
                                    <div className="mt-2 d-flex gap-2 flex-wrap">
                                        {op.nbInscriptions?.pending > 0 && (
                                            <Badge bg="warning" text="dark">
                                                {op.nbInscriptions.pending} {inscriptionStatusLabel['pending']}
                                            </Badge>
                                        )}
                                        {op.nbInscriptions?.validated > 0 && (
                                            <Badge bg="info">
                                                {op.nbInscriptions.validated} {inscriptionStatusLabel['validated']}
                                            </Badge>
                                        )}
                                        {op.nbInscriptions?.injected > 0 && (
                                            <Badge bg="dark">
                                                {op.nbInscriptions.injected} {inscriptionStatusLabel['injected']}
                                            </Badge>
                                        )}
                                        <Badge bg="light" text="dark" className="border">
                                            {op.nbInscriptions?.total ?? 0} total
                                        </Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            }
        </Container>
    );
};

export default OperationsPage;
